import styled from 'styled-components/macro';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { ChannelMessageAttachmentType } from 'shared/types/graphql';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { Modal, ModalBody, ModalCloseButton } from '@televet/kibble-ui/build/components/Modal';
import { ModalContent } from '@televet/kibble-ui/build/chakra';

interface IChatMediaPreviewModalProps {
  setPreviewData?: React.Dispatch<
    React.SetStateAction<{
      previewFileUrl: string;
      previewAttachmentType: ChannelMessageAttachmentType;
      previewFileName: string;
    }>
  >;
  stagedFile?: File;
  fileURL?: string;
  attachmentType?: ChannelMessageAttachmentType;
  fileName?: string;
}

const MAX_SCALE_VALUE = 1.8;
const MAX_SCALE_VALUE_MOBILE = 2.8;
const DEFAULT_SCALE_VALUE = 1;

const ChatMediaPreviewModal = ({
  setPreviewData,
  fileURL,
  stagedFile,
  attachmentType,
  fileName,
}: IChatMediaPreviewModalProps): ReactElement | null => {
  const [shouldModalOpen, setShouldModalOpen] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const { isMobile } = useResolutionProvider();

  const maxScaleValue = isMobile ? MAX_SCALE_VALUE_MOBILE : MAX_SCALE_VALUE;
  const imageRef = useRef(null);
  useEffect(() => {
    setShouldModalOpen(fileURL && fileURL?.length > 0 ? true : false);
  }, [fileURL]);

  useEffect(() => {
    if (fileName) {
      const extension = fileName?.slice().substring(fileName.lastIndexOf('.') + 1);

      if (['doc', 'zip', '7z'].some((el) => extension.includes(el))) {
        setTimeout(() => {
          setShouldModalOpen(false);
        }, 500);
      }
    }
  }, [fileName]);

  const onCloseModal = (): void => {
    setPreviewData &&
      setPreviewData(() => ({
        previewFileUrl: '',
        previewAttachmentType: ChannelMessageAttachmentType.File,
        previewFileName: '',
      }));
    setShouldModalOpen(false);
  };

  const scaleFunction = (
    scale: number,
    setTranform: (x: number, y: number, newScale: number) => void,
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    positionX: number,
    positionY: number,
  ): void => {
    const zoomer = event.currentTarget.getBoundingClientRect();
    const mouseX = (event.clientX - zoomer.left) / scale;
    const mouseY = (event.clientY - zoomer.top) / scale;
    const scaleDifference = scale === DEFAULT_SCALE_VALUE ? maxScaleValue - DEFAULT_SCALE_VALUE : 0;
    const calculatedPositionX = positionX - mouseX * scaleDifference;
    const calculatedPositionY = positionY - mouseY * scaleDifference;

    if (scale === maxScaleValue && !isDragging) {
      setTranform(0, 0, DEFAULT_SCALE_VALUE);
      return;
    }

    setTranform(calculatedPositionX, calculatedPositionY, maxScaleValue);
  };

  return (
    <Modal isCentered isOpen={shouldModalOpen} onClose={onCloseModal} useFullScreenMobile size="7xl">
      <ModalContent backdropFilter="blur(12px)" backgroundColor="background.backdrop" w="97%" h="97%">
        <ModalCloseButton size="lg" variant="secondary" pos="absolute" top={5} right={5} zIndex="banner" isOnContrast />
        <ModalBody display="flex" justifyContent="center" alignItems="center">
          <Alignment>
            {attachmentType === ChannelMessageAttachmentType.Image && (
              <TransformWrapper
                defaultScale={DEFAULT_SCALE_VALUE}
                options={{ maxScale: maxScaleValue }}
                doubleClick={{ disabled: true }}
                onPanning={(): void => {
                  setIsDragging(true);
                }}
                onPanningStop={(): void => {
                  setTimeout(() => {
                    setIsDragging(false);
                  }, 500);
                }}
              >
                {({
                  scale,
                  setTransform,
                  positionX,
                  positionY,
                }: {
                  scale: number;
                  setTransform: (x: number, y: number, newScale: number) => void;
                  positionX: number;
                  positionY: number;
                }): JSX.Element => (
                  <div
                    draggable={true}
                    onClick={(event): void => {
                      scaleFunction(scale, setTransform, event, positionX, positionY);
                    }}
                  >
                    <TransformComponent>
                      <PreviewImageContainer
                        isMobile={isMobile}
                        draggable={true}
                        zoomType={scale === DEFAULT_SCALE_VALUE ? 'zoom-in' : isDragging ? 'grab' : 'zoom-out'}
                      >
                        <PreviewImage
                          ref={imageRef}
                          draggable={true}
                          src={fileURL}
                          alt={stagedFile?.name || 'File name could not be found'}
                        />
                      </PreviewImageContainer>
                    </TransformComponent>
                  </div>
                )}
              </TransformWrapper>
            )}
            {attachmentType === ChannelMessageAttachmentType.Video && (
              <PreviewVideo src={fileURL} controls>
                Your browser does not support the video tag.
              </PreviewVideo>
            )}
            {attachmentType === ChannelMessageAttachmentType.File && (
              <PreviewIframe allow="fullscreen" title={stagedFile?.name || attachmentType} src={fileURL} />
            )}
          </Alignment>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ChatMediaPreviewModal;

const PreviewImage = styled.img`
  max-height: 100%;
`;

const PreviewImageContainer = styled.div<{ zoomType: string; isMobile: boolean }>`
  width: 97vw;
  height: ${({ isMobile }): string => (isMobile ? '80vh' : '88vh')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: -moz-${({ zoomType }): string => zoomType};
  cursor: -webkit-${({ zoomType }): string => zoomType};
  cursor: ${({ zoomType }): string => zoomType};
`;

const PreviewVideo = styled.video`
  max-width: 100%;
  height: 100%;
  max-height: 600px;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: auto;
  max-height: 600px;
  min-height: 500px;
  max-height: 600px;
`;

const Alignment = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;
